import { useState, useEffect } from 'react';
import type { Room, User } from '../types';
import { api } from '../api/client';
import { useRoomsStore } from '../store';

interface NewChatModalProps {
  onClose: () => void;
  onRoomCreated: (room: Room) => void;
}

type Tab = 'personal' | 'group';

export function NewChatModal({ onClose, onRoomCreated }: NewChatModalProps) {
  const [tab, setTab] = useState<Tab>('personal');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [groupName, setGroupName] = useState('');
  const [groupDesc, setGroupDesc] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const { upsertRoom } = useRoomsStore();

  useEffect(() => {
    if (search.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { users } = await api.users.search(search);
        setSearchResults(users);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const toggleUser = (u: User) => {
    if (tab === 'personal') {
      setSelectedUsers([u]);
    } else {
      setSelectedUsers(prev =>
        prev.find(p => p.id === u.id) ? prev.filter(p => p.id !== u.id) : [...prev, u]
      );
    }
  };

  const handleCreate = async () => {
    if (selectedUsers.length === 0) return;
    setLoading(true);
    try {
      const { room } = await api.rooms.create({
        type: tab,
        name: tab === 'group' ? groupName : undefined,
        description: tab === 'group' ? groupDesc : undefined,
        memberIds: selectedUsers.map(u => u.id),
      });
      upsertRoom(room);
      onRoomCreated(room);
      onClose();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Gagal membuat chat');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 animate-fade-in" onClick={onClose}>
      <div
        className="glass rounded-3xl w-full max-w-md shadow-2xl animate-slide-up overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-dark-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-white">Chat Baru</h2>
            <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/[0.06] text-dark-400 hover:text-white transition-all">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            {(['personal', 'group'] as Tab[]).map(t => (
              <button
                key={t}
                onClick={() => { setTab(t); setSelectedUsers([]); }}
                className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${
                  tab === t ? 'bg-brand-600 text-white' : 'text-dark-400 hover:text-white hover:bg-white/[0.06]'
                }`}
              >
                {t === 'personal' ? '💬 Personal' : '👥 Grup'}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Group fields */}
          {tab === 'group' && (
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Nama grup *"
                value={groupName}
                onChange={e => setGroupName(e.target.value)}
                className="input-base text-sm"
              />
              <input
                type="text"
                placeholder="Deskripsi grup (opsional)"
                value={groupDesc}
                onChange={e => setGroupDesc(e.target.value)}
                className="input-base text-sm"
              />
            </div>
          )}

          {/* Selected users */}
          {selectedUsers.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {selectedUsers.map(u => (
                <div key={u.id} className="flex items-center gap-1.5 bg-brand-600/20 border border-brand-500/30 rounded-full px-3 py-1">
                  <span className="text-sm text-brand-300">{u.name}</span>
                  <button onClick={() => toggleUser(u)} className="text-brand-400 hover:text-white">
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Cari pengguna (nama atau email)..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input-base pl-9 text-sm"
            />
          </div>

          {/* Results */}
          <div className="space-y-1">
            {searching && (
              <div className="text-center py-4 text-dark-400 text-sm">Mencari…</div>
            )}
            {!searching && search.length >= 2 && searchResults.length === 0 && (
              <div className="text-center py-4 text-dark-400 text-sm">Pengguna tidak ditemukan</div>
            )}
            {searchResults.map(u => {
              const selected = !!selectedUsers.find(s => s.id === u.id);
              return (
                <div
                  key={u.id}
                  onClick={() => toggleUser(u)}
                  className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                    selected ? 'bg-brand-600/20 border border-brand-500/30' : 'hover:bg-white/[0.04]'
                  }`}
                >
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt="" className="w-9 h-9 rounded-full" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center text-white text-sm font-bold">
                      {u.name[0]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{u.name}</p>
                    <p className="text-dark-400 text-xs truncate">{u.email}</p>
                  </div>
                  {selected && (
                    <div className="w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <button
            onClick={handleCreate}
            disabled={loading || selectedUsers.length === 0 || (tab === 'group' && !groupName.trim())}
            className="btn-primary w-full"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Membuat…
              </div>
            ) : tab === 'personal' ? 'Mulai Chat' : `Buat Grup (${selectedUsers.length} anggota)`}
          </button>
        </div>
      </div>
    </div>
  );
}
